// @ts-nocheck
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toggleAddOnForPassenger } from '../../../../redux/slices/booking/booking';
import { formatCurrency } from '../../../../utils/currencyHelper';
import { planIsSoldOut, planIsPastDeadline } from '../../../../utils/booking';
import { ErrorToast } from '../../../../utils/alerts';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { WarningCircle, XCircle } from '@phosphor-icons/react';
import Editor from '../../../Editor/Editor';
import PropTypes from 'prop-types';
import {
  getPassengerAddOnAmount,
  getPassengerPlanTitle,
  isPercentageBasedAddOn
} from '../../../../utils/percentageAddOns';
import './BookingAddOnCard.css';

/**
 * BookingAddOnCard component for displaying and selecting trip add-ons
 *
 * @namespace Components
 *
 * @param {Object} props - Component props
 * @param {Object} props.addon - The add-on data
 * @param {Array} props.passengers - List of passenger forms from step 2
 * @param {...any} props.rest - Additional props to be spread on the parent element
 *
 * @returns {JSX.Element} React element representing the booking add-on card
 */
const BookingAddOnCard = ({ addon, passengers, ...rest }) => {
  const { t } = useTranslation(['trip', 'common']);
  const dispatch = useDispatch();
  const { enablePercentageBasedAddons } = useFlags();
  const bookingPlans = useSelector((state) => state.booking.bookingplans || []);
  const allAddOns = useSelector((state) => state.booking.addons || []);
  const selectedAddOns = useSelector((state) => state.booking.selectedAddOns);
  const isPercentagePricing = isPercentageBasedAddOn(
    addon,
    enablePercentageBasedAddons
  );

  // Check if this add-on is sold out or past deadline
  const isSoldOut = planIsSoldOut(addon);
  const isPastDeadline = planIsPastDeadline(addon);
  // Disable the card if sold out OR past deadline (regardless of auto-hide settings)
  const isDisabled = isSoldOut || isPastDeadline;

  // Get selected passengers for this add-on
  const selectedAddOn = selectedAddOns.find(
    (selected) => selected.tripaddonid === addon?.tripaddonid
  );
  const selectedPassengerIds = selectedAddOn?.passengerFormIds || [];

  // Calculate remaining capacity for this add-on
  const hasLimitedAvailability = addon?.maxavailable && addon.maxavailable > 0;
  const numSelected = selectedPassengerIds.length;
  // Backend returns 'available' field (already calculated as maxavailable - sold)
  const backendAvailable = addon?.available ?? null;
  const remainingCapacity =
    hasLimitedAvailability && backendAvailable !== null
      ? backendAvailable - numSelected
      : Infinity;

  const handlePassengerToggle = (passengerFormId) => {
    if (isDisabled) return;

    const isCurrentlySelected = selectedPassengerIds.includes(passengerFormId);

    // If trying to select and no capacity remaining, don't allow
    if (!isCurrentlySelected && remainingCapacity <= 0) return;

    // Prevent selecting negative-priced add-ons when it would make booking total negative.
    if (!isCurrentlySelected) {
      const selectedPlansTotal = bookingPlans.reduce((sum, plan) => {
        const quantity = Number(plan?.selectedQuantity) || 0;
        const price = Number(plan?.priceperperson) || 0;
        return sum + quantity * price;
      }, 0);

      const selectedAddOnsTotal = selectedAddOns.reduce((sum, selected) => {
        const selectedAddon = allAddOns.find(
          (a) => a.tripaddonid === selected.tripaddonid
        );
        if (!selectedAddon) return sum;
        return (
          sum +
          selected.passengerFormIds.reduce((passengerTotal, formId) => {
            const passenger = passengers.find((p) => p.formId === formId);
            return (
              passengerTotal +
              getPassengerAddOnAmount({
                addon: selectedAddon,
                passenger,
                bookingPlans,
                enablePercentageBasedAddons
              })
            );
          }, 0)
        );
      }, 0);

      const currentTotal = selectedPlansTotal + selectedAddOnsTotal;
      const passenger = passengers.find((p) => p.formId === passengerFormId);
      const addOnDelta = getPassengerAddOnAmount({
        addon,
        passenger,
        bookingPlans,
        enablePercentageBasedAddons
      });

      if (currentTotal + addOnDelta < 0) {
        ErrorToast(
          t(
            'trip:addOnWouldMakeTotalNegative',
            'This add-on cannot be selected because it would make the booking total less than $0.00.'
          )
        );
        return;
      }
    }

    dispatch(
      toggleAddOnForPassenger({
        tripaddonid: addon?.tripaddonid,
        passengerFormId
      })
    );
  };

  const isPassengerSelected = (passengerFormId) => {
    return selectedPassengerIds.includes(passengerFormId);
  };

  // Format price with sign
  const formatPrice = (price) => {
    const formattedPrice = formatCurrency(Math.abs(price));
    return price < 0 ? `-${formattedPrice}` : formattedPrice;
  };

  return (
    <Box
      className={`booking-addon-card ${isDisabled ? 'booking-addon-card--disabled' : ''}`}
      data-testid='booking-addon-card'
      {...rest}
    >
      <Box className='booking-addon-card__content'>
        {/* Header Row: Title → Price */}
        <Box className='booking-addon-card__header-row'>
          <Box className='booking-addon-card__title-section'>
            <h3 className='booking-addon-card__title'>{addon?.title}</h3>
          </Box>
          {!isPercentagePricing && (
            <Box className='booking-addon-card__price'>
              <span className='booking-addon-card__price-text'>
                {formatPrice(addon?.priceperperson || 0)}
              </span>
            </Box>
          )}
        </Box>

        {/* Description */}
        {addon?.description && addon.description !== '' && (
          <Box className='booking-addon-card__description'>
            <Editor
              content={addon.description}
              editable={false}
              isPreviewing={true}
              id={`booking-addon-card-description-editor-${addon?.tripaddonid}`}
              className='third-output-editor--full-width'
            />
          </Box>
        )}

        {/* Status Badges */}
        {(isPastDeadline || isSoldOut) && (
          <Box className='booking-addon-card__badges'>
            {isPastDeadline && (
              <span className='addon-pastdeadline-badge'>
                <WarningCircle size={16} />
                {t('trip:bookingDeadlinePassed', 'Booking Deadline Passed')}
              </span>
            )}
            {isSoldOut && (
              <span className='addon-soldout-badge'>
                <XCircle size={16} />
                {t('trip:soldOut', 'Sold Out')}
              </span>
            )}
          </Box>
        )}

        {/* Passenger Selection */}
        {!isDisabled && passengers && passengers.length > 0 && (
          <Box className='booking-addon-card__passengers-section'>
            <hr className='booking-addon-card__separator' />
            <label className='booking-addon-card__passengers-label'>
              {t('trip:selectPassengers', 'Select passengers:')}
            </label>
            <Box
              className={`booking-addon-card__passengers-grid ${
                isPercentagePricing
                  ? 'booking-addon-card__passengers-grid--percentage'
                  : ''
              }`}
            >
              {passengers.map((passenger, index) => {
                const passengerFormId =
                  passenger.formId || passenger.id || `passenger-${index}`;

                // Extract first and last name from fields array
                const fields = passenger.fields || [];
                const firstNameField = fields.find(
                  (f) =>
                    f.fieldId === 'firstName' ||
                    f.title?.toLowerCase() === 'first name'
                );
                const lastNameField = fields.find(
                  (f) =>
                    f.fieldId === 'lastName' ||
                    f.title?.toLowerCase() === 'last name'
                );

                const firstName = firstNameField?.value || '';
                const lastName = lastNameField?.value || '';

                const passengerName =
                  firstName && lastName
                    ? `${firstName} ${lastName}`
                    : firstName ||
                      lastName ||
                      `${t('trip:passenger', 'Passenger')} ${index + 1}`;

                const isSelected = isPassengerSelected(passengerFormId);
                // Disable checkbox if: not selected AND no remaining capacity
                const isCheckboxDisabled =
                  !isSelected && remainingCapacity <= 0;
                const planTitle = getPassengerPlanTitle(
                  passenger,
                  bookingPlans
                );
                const calculatedAmount = getPassengerAddOnAmount({
                  addon,
                  passenger,
                  bookingPlans,
                  enablePercentageBasedAddons
                });
                const label = isPercentagePricing ? (
                  <Box className='booking-addon-card__passenger-row-label'>
                    <span className='booking-addon-card__passenger-name'>
                      {passengerName}
                    </span>
                    <span className='booking-addon-card__passenger-plan'>
                      {planTitle}
                    </span>
                    <span className='booking-addon-card__passenger-price'>
                      {formatCurrency(calculatedAmount)}
                    </span>
                  </Box>
                ) : (
                  passengerName
                );

                return (
                  <FormControlLabel
                    key={passengerFormId}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handlePassengerToggle(passengerFormId)}
                        disabled={isCheckboxDisabled}
                        sx={{
                          color: 'var(--gray-400, #98a2b3)',
                          '&.Mui-checked': {
                            color: 'var(--primary-600, #0d69d4)'
                          },
                          '&.Mui-disabled': {
                            color: 'var(--gray-300, #d0d5dd)'
                          }
                        }}
                      />
                    }
                    label={label}
                    className={`booking-addon-card__passenger-checkbox ${
                      isPercentagePricing
                        ? 'booking-addon-card__passenger-checkbox--percentage'
                        : ''
                    }`}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

BookingAddOnCard.propTypes = {
  addon: PropTypes.object.isRequired,
  passengers: PropTypes.array
};

export default BookingAddOnCard;
